/**
 * Module-scoped cache + prefetch for the Conference Hub queries.
 * Calling prefetchConferenceHub() from a hover/focus handler kicks off the
 * network requests; ConferenceHub.tsx then re-uses the in-flight promises so
 * navigation feels instant.
 */
import { supabase } from "@/integrations/supabase/client";
import { trackAsync } from "@/lib/perfLog";
import type {
  PublicConferenceRow,
  PublicKitEventRow,
} from "@/lib/conferencePublic";

export interface ConferenceHubRegion {
  id: string;
  name: string;
  color?: string | null;
  description?: string | null;
  total_members?: number | null;
  states?: string[] | null;
  universities?: string[] | null;
  prayer_cells?: number | null;
  bible_studies?: number | null;
  conference_start_date?: string | null;
}

export interface ConferenceHubBundle {
  regions: ConferenceHubRegion[];
  conferences: PublicConferenceRow[];
  kitEvents: PublicKitEventRow[];
  counts: any[];
  header: Record<string, any> | null;
}

interface CacheEntry<T> {
  promise: Promise<T>;
  value?: T;
  ts: number;
}

const FRESH_MS = 60_000;
const cache: Partial<{
  regions: CacheEntry<ConferenceHubRegion[]>;
  conferences: CacheEntry<PublicConferenceRow[]>;
  kitEvents: CacheEntry<PublicKitEventRow[]>;
  counts: CacheEntry<any[]>;
  header: CacheEntry<Record<string, any> | null>;
  pastFirstPage: CacheEntry<PublicConferenceRow[]>;
}> = {};

const isFresh = (entry?: CacheEntry<any>) =>
  entry && Date.now() - entry.ts < FRESH_MS;

const memoize = <K extends keyof typeof cache, T>(
  key: K,
  loader: () => Promise<T>,
): Promise<T> => {
  const existing = cache[key] as CacheEntry<T> | undefined;
  if (existing && (isFresh(existing) || existing.value === undefined)) {
    return existing.promise;
  }
  const promise = loader().then((value) => {
    cache[key] = { promise: Promise.resolve(value), value, ts: Date.now() } as any;
    return value;
  });
  cache[key] = { promise, ts: Date.now() } as any;
  return promise;
};

export const fetchRegions = () =>
  memoize("regions", () =>
    trackAsync("conference-hub", "regions", async () => {
      const { data, error } = await supabase
        .from("regions")
        .select(
          "id, name, color, description, total_members, states, universities, prayer_cells, bible_studies, conference_start_date",
        )
        .order("name");
      if (error) throw error;
      return (data as ConferenceHubRegion[]) || [];
    }, { timeoutMs: 15000 }),
  );

export const fetchUpcomingConferences = () =>
  memoize("conferences", () =>
    trackAsync("conference-hub", "conferences:upcoming", async () => {
      // Keep facilitators/contact_name because public cards must mirror Latest Conference speaker/contact edits.
      const { data, error } = await supabase
        .from("conferences")
        .select(
          "id, region_id, conference_name, conference_date, conference_dates, location, flyer_url, is_active, status, slogan, description, facilitators, contact_phone, contact_name",
        )
        .eq("is_active", true)
        .eq("status", "upcoming")
        .ilike("conference_name", "%2026%");
      if (error) throw error;
      return (data as PublicConferenceRow[]) || [];
    }, { timeoutMs: 15000 }),
  );

export const fetchKitEvents = () =>
  memoize("kitEvents", () =>
    trackAsync("conference-hub", "kit-events", async () => {
      const { data, error } = await supabase
        .from("conference_kit_events")
        .select(
          "region_id, title, theme, scripture, venue_name, venue_address, city, state, start_date, end_date, start_time, end_time, conference_year, flyer_url, flyer_approved, status, coordinator_name, coordinator_email, coordinator_phone, registration_url, speaker_name, speaker_title, updated_at",
        )
        .eq("conference_year", 2026)
        .neq("status", "archived");
      if (error) throw error;
      return (data as PublicKitEventRow[]) || [];
    }, { timeoutMs: 15000 }),
  );

const loadRegistrationCounts = () =>
  trackAsync("conference-hub", "rpc:get_public_registration_counts", async () => {
    const { data, error } = await supabase.rpc(
      "get_public_registration_counts" as any,
    );
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  }, { timeoutMs: 15000 });

export const fetchCounts = () => memoize("counts", loadRegistrationCounts);

export const refreshCounts = async () => {
  const value = await loadRegistrationCounts();
  cache.counts = { promise: Promise.resolve(value), value, ts: Date.now() };
  return value;
};

export const fetchHeader = () =>
  memoize("header", () =>
    trackAsync("conference-hub", "app-settings:header", async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "latest_conference_header")
        .maybeSingle();
      if (error) throw error;
      return (data?.value as Record<string, any>) || null;
    }, { timeoutMs: 15000 }),
  );

/** First page of past conferences — keeps initial payload small. */
export const fetchPastConferencesFirstPage = (pageSize = 40) =>
  memoize("pastFirstPage", () =>
    trackAsync(
      "conference-hub",
      "conferences:past:first-page",
      async () => {
        const { data, error } = await supabase
          .from("conferences")
          // Narrow columns — past list only needs name + dates
          .select(
            "id, region_id, conference_name, conference_date, conference_dates, status, flyer_url",
          )
          .or("status.eq.completed,status.eq.past")
          .order("conference_date", { ascending: false, nullsFirst: false })
          .range(0, pageSize - 1);
        if (error) throw error;
        return (data as PublicConferenceRow[]) || [];
      },
      { timeoutMs: 15000, meta: { pageSize } },
    ),
  );

export const fetchPastConferencesPage = (page: number, pageSize = 40) =>
  trackAsync(
    "conference-hub",
    `conferences:past:page-${page}`,
    async () => {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, error } = await supabase
        .from("conferences")
        .select(
          "id, region_id, conference_name, conference_date, conference_dates, status, flyer_url",
        )
        .or("status.eq.completed,status.eq.past")
        .order("conference_date", { ascending: false, nullsFirst: false })
        .range(from, to);
      if (error) throw error;
      return (data as PublicConferenceRow[]) || [];
    },
    { timeoutMs: 15000, meta: { page, pageSize } },
  );

/** Fire-and-forget — used by link hover/focus to warm the cache. */
export const prefetchConferenceHub = () => {
  void fetchRegions();
  void fetchUpcomingConferences();
  void fetchKitEvents();
  void fetchCounts();
  void fetchHeader();
  void fetchPastConferencesFirstPage();
};

export const invalidateConferenceHubCache = () => {
  (Object.keys(cache) as (keyof typeof cache)[]).forEach((k) => {
    delete cache[k];
  });
};
